import { Link, useSearchParams } from 'react-router-dom'
import type { ReactNode } from 'react'
import { AlertCircle, ArrowLeft, FileText, Mail } from 'lucide-react'
import { LegalHtml } from '@/components/legal/LegalHtml'
import {
  useLegalDocument,
  type LegalDocumentKind,
  type PublicLegalDocument,
} from '@/hooks/useLegalDocument'
import { buildLoginHref } from '@/lib/return-to'

const TITLES: Record<LegalDocumentKind, string> = {
  privacy: 'Privacy Notice',
  terms: 'Terms of Use',
}

const PLACEHOLDER_COPY =
  'This page is pending CyberFind legal review. Final documents will be published once approved.'

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function hasDifferentEffectiveDate(document: Extract<PublicLegalDocument, { published: true }>) {
  return formatDate(document.effectiveAt) !== formatDate(document.publishedAt)
}

function LegalPageShell({
  kind,
  children,
}: {
  kind: LegalDocumentKind
  children: ReactNode
}) {
  const [params] = useSearchParams()
  const backHref = buildLoginHref(params.get('returnTo'))

  return (
    <div className="h-screen overflow-y-auto bg-background text-foreground">
      <main className="mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-5 border-b border-border pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-4">
            <span
              className="block"
              role="img"
              aria-label="CyberFind"
              data-testid="cyberfind-legal-brand-mark"
            >
              <img
                src="/icons/cyberfind-logo-light.svg"
                alt=""
                aria-hidden
                className="h-11 w-auto dark:hidden"
              />
              <img
                src="/icons/cyberfind-logo-dark.svg"
                alt=""
                aria-hidden
                className="hidden h-11 w-auto dark:block"
              />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                CyberFind legal
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-foreground">
                {TITLES[kind]}
              </h1>
            </div>
          </div>
          <Link
            to={backHref}
            className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to sign in
          </Link>
        </header>

        {children}

        <footer className="mt-auto flex flex-col gap-3 border-t border-border py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <a
            href="mailto:support@cyberfind.io"
            className="inline-flex min-h-11 items-center gap-2 font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Mail className="h-4 w-4" aria-hidden />
            support@cyberfind.io
          </a>
        </footer>
      </main>
    </div>
  )
}

function PlaceholderState() {
  return (
    <section className="flex flex-1 items-center py-12">
      <div className="max-w-2xl space-y-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted">
          <FileText className="h-5 w-5 text-muted-foreground" aria-hidden />
        </div>
        <p className="max-w-prose text-base leading-7 text-muted-foreground">
          {PLACEHOLDER_COPY}
        </p>
      </div>
    </section>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <section className="flex flex-1 items-center py-12">
      <div className="max-w-2xl space-y-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-destructive/10">
          <AlertCircle className="h-5 w-5 text-destructive" aria-hidden />
        </div>
        <div className="space-y-3">
          <div className="space-y-3" role="alert">
            <p className="max-w-prose text-base font-medium leading-7 text-foreground">
              Unable to load document
            </p>
            <p className="max-w-prose text-sm leading-6 text-muted-foreground">
              We encountered an error while loading this legal document. Please try again or contact
              support if the issue persists.
            </p>
          </div>
          <button
            onClick={onRetry}
            className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    </section>
  )
}

function PublishedDocument({
  document,
}: {
  document: Extract<PublicLegalDocument, { published: true }>
}) {
  const showEffective = hasDifferentEffectiveDate(document)
  return (
    <article className="py-8">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <time dateTime={document.publishedAt}>
          Published {formatDate(document.publishedAt)}
        </time>
        {showEffective ? (
          <time dateTime={document.effectiveAt}>
            Effective {formatDate(document.effectiveAt)}
          </time>
        ) : null}
      </div>

      <section className="mt-5 rounded-md border border-border bg-muted/40 p-4">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          Latest update
        </p>
        <p className="mt-2 text-sm leading-6 text-foreground">
          {document.latestChangeNote}
        </p>
      </section>

      <LegalHtml
        html={document.sanitizedHtml}
        className="mt-8 max-w-3xl space-y-5 text-base leading-7 text-foreground [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:pt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:pt-3 [&_h3]:text-lg [&_h3]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:max-w-prose [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-4 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2 [&_ul]:list-disc"
      />
    </article>
  )
}

export function LegalPage({ kind }: { kind: LegalDocumentKind }) {
  const query = useLegalDocument(kind)
  const document = query.data

  return (
    <LegalPageShell kind={kind}>
      {query.isLoading ? (
        <section className="flex flex-1 items-center py-12" aria-busy="true">
          <div className="h-32 w-full max-w-2xl rounded-md bg-muted" />
        </section>
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : !document || !document.published ? (
        <PlaceholderState />
      ) : (
        <PublishedDocument document={document} />
      )}
    </LegalPageShell>
  )
}
